import type { FC, ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { Link } from '@packages/modules/src/Link/Link';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';

const messages = defineMessages({
  callUs: {
    id: 'search.needHelpSection.callUs',
    defaultMessage: 'Rufe uns an',
  },
  description: {
    id: 'search.needHelpSection.description',
    defaultMessage:
      'Du hast konkrete Fragen und wünschst dir von uns eine Antwort? Wende dich bitte an unsere Kundenbetreuung.',
  },
  email: {
    id: 'search.needHelpSection.email',
    defaultMessage: 'service@baur.de',
  },
  title: {
    id: 'search.needHelpSection.title',
    defaultMessage: 'Du brauchst Hilfe?',
  },
  writeUs: {
    id: 'search.needHelpSection.writeUs',
    defaultMessage: 'Schreib uns',
  },
  phoneNumber: {
    id: 'search.needHelpSection.phoneNumber',
    defaultMessage: '09572 5050',
  },
  openHours: {
    id: 'search.needHelpSection.openHours',
    defaultMessage: 'täglich von 06.00 bis 23.00 Uhr',
  },
});

/**
 * HelpSubSection which provide a Container with same spacing for each subsection in the HelpSection
 */
const HelpSubSection = ({ children }: { children: ReactNode }) => (
  <div className="text-size-body-sm mt-2 max-w-[450px]">{children}</div>
);

/**
 * NeedHelpSection component displays a section with help information such as:
 * - Phone number to call
 * - E-Mail to write to
 * - Opening hours
 * */
export const NeedHelpSection: FC = () => {
  const { formatMessage, locale } = useIntl();
  const {
    search: { hideTelekomIcon },
  } = useConfig();

  return (
    <Card data-testid="need-help-section">
      <CardContent className="pb-3">
        <h5 className="mb-1">
          <FormattedMessage key="search.needHelpSection.title" {...messages.title} />
        </h5>
        <Divider className="-mx-2" />
        <HelpSubSection>
          <FormattedMessage key="search.needHelpSection.description" {...messages.description} />
        </HelpSubSection>
        <HelpSubSection>
          <p className="text-size-body-sm leading-3">
            <FormattedMessage key="search.needHelpSection.writeUs" {...messages.writeUs} />
            <br />
            <Link
              color="inherit"
              href={`mailto:${formatMessage(messages.email)}`}
              className="font-bold hover:underline"
            >
              <FormattedMessage key="search.needHelpSection.email" {...messages.email} />
            </Link>
          </p>
        </HelpSubSection>
        <HelpSubSection>
          <p className="text-size-body-sm leading-3">
            <FormattedMessage key="search.needHelpSection.callUs" {...messages.callUs} />
          </p>
          <div className="flex items-baseline">
            <p className="text-size-body-sm leading-3">
              <Link
                color="inherit"
                className="font-bold hover:underline"
                href={`tel:${formatMessage(messages.phoneNumber)}`}
              >
                <FormattedMessage
                  key="search.needHelpSection.phoneNumber"
                  {...messages.phoneNumber}
                />
              </Link>
            </p>
            {locale === 'de-DE' && !hideTelekomIcon && (
              <Image
                src="/search-assets/telekom.png"
                alt="Telekom"
                height={12}
                width={25}
                loading="lazy"
                style={{ marginLeft: '0.25rem' }}
                unoptimized
              />
            )}
          </div>
          <p className="text-size-body-sm leading-3">
            <FormattedMessage key="search.needHelpSection.openHours" {...messages.openHours} />
          </p>
        </HelpSubSection>
      </CardContent>
    </Card>
  );
};
